import React, { useState, useMemo } from 'react';
import { CompleteKundali, Language, PlanetName } from '../types/kundali';
import {
  analyzeVedicGemology,
  auditJewelryPairings,
  MASTER_GEMSTONES,
  JewelryAuditResult
} from '../utils/vedicGemologyEngine';
import {
  AlertTriangle,
  CheckCircle2,
  Gem,
  Info,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  X,
  Plus,
  RefreshCw,
  Zap,
  HelpCircle
} from 'lucide-react';

interface JewelryConflictCheckerProps {
  kundali: CompleteKundali;
  lang?: Language;
}

const AVAILABLE_STONES: { planet: PlanetName; label: string; gemstone: string; color: string }[] = [
  { planet: 'Sun', label: 'Sun (Surya)', gemstone: 'Ruby (Manikya)', color: '#E11D48' },
  { planet: 'Moon', label: 'Moon (Chandra)', gemstone: 'Pearl (Moti)', color: '#94A3B8' },
  { planet: 'Mars', label: 'Mars (Mangal)', gemstone: 'Red Coral (Moonga)', color: '#DC2626' },
  { planet: 'Mercury', label: 'Mercury (Budha)', gemstone: 'Emerald (Panna)', color: '#059669' },
  { planet: 'Jupiter', label: 'Jupiter (Guru)', gemstone: 'Yellow Sapphire (Pukhraj)', color: '#EAB308' },
  { planet: 'Venus', label: 'Venus (Shukra)', gemstone: 'Diamond / Opal (Heera)', color: '#38BDF8' },
  { planet: 'Saturn', label: 'Saturn (Shani)', gemstone: 'Blue Sapphire (Neelam)', color: '#2563EB' },
  { planet: 'Rahu', label: 'Rahu', gemstone: 'Hessonite (Gomed)', color: '#B45309' },
  { planet: 'Ketu', label: 'Ketu', gemstone: "Cat's Eye (Lehsuniya)", color: '#84CC16' }
];

export const JewelryConflictChecker: React.FC<JewelryConflictCheckerProps> = ({
  kundali,
  lang = 'en'
}) => {
  const analysis = useMemo(() => analyzeVedicGemology(kundali), [kundali]);

  // Selected stones user is currently wearing (up to 3)
  const [selectedPlanets, setSelectedPlanets] = useState<PlanetName[]>(['Sun', 'Saturn']);

  const auditResult: JewelryAuditResult = useMemo(() => {
    return auditJewelryPairings(selectedPlanets, analysis);
  }, [selectedPlanets, analysis]);

  const togglePlanet = (planet: PlanetName) => {
    if (selectedPlanets.includes(planet)) {
      setSelectedPlanets(selectedPlanets.filter(p => p !== planet));
    } else {
      if (selectedPlanets.length >= 3) {
        // Replace oldest or cap at 3
        setSelectedPlanets([...selectedPlanets.slice(1), planet]);
      } else {
        setSelectedPlanets([...selectedPlanets, planet]);
      }
    }
  };

  const getStatusBadge = (status: 'MUTUAL_FRIENDS' | 'NEUTRAL' | 'MUTUAL_ENEMIES' | 'SEVERE_HOSTILE') => {
    switch (status) {
      case 'MUTUAL_FRIENDS':
        return {
          label: 'Auspicious Friends (Mitra)',
          className: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20'
        };
      case 'NEUTRAL':
        return {
          label: 'Neutral Harmonic (Sama)',
          className: 'bg-teal-500/10 text-teal-700 border-teal-500/20'
        };
      case 'MUTUAL_ENEMIES':
        return {
          label: 'Planetary Opposition (Shatru)',
          className: 'bg-amber-500/10 text-amber-700 border-amber-500/20'
        };
      case 'SEVERE_HOSTILE':
        return {
          label: '⚠️ Destructive Clash (Ghora Virodha)',
          className: 'bg-rose-500/10 text-rose-700 border-rose-500/30'
        };
    }
  };

  return (
    <div className="space-y-6">
      {/* Intro Header */}
      <div className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-2xs space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-600" />
            <h3 className="font-serif font-bold text-lg text-astro-text">
              "What Are You Currently Wearing?" • Gemstone Conflict Auditor
            </h3>
          </div>
          <span className="text-xs text-astro-secondary-text">
            Select up to 3 stones you currently wear on rings or pendants
          </span>
        </div>

        <p className="text-xs text-astro-secondary-text leading-relaxed">
          In Vedic astrology, wearing contradictory gemstones simultaneously creates competing electromagnetic vibrations in your aura. For instance, pairing <strong>Ruby (Sun) with Blue Sapphire (Saturn)</strong> or <strong>Pearl (Moon) with Hessonite (Rahu)</strong> causes catastrophic karmic interference. Test your current jewelry combinations below.
        </p>

        {/* Planet Selection Grid */}
        <div className="pt-2">
          <div className="text-[11px] font-bold text-astro-secondary-text uppercase tracking-wider mb-2">
            Click to select or deselect gemstones you currently wear ({selectedPlanets.length}/3 chosen):
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2.5">
            {AVAILABLE_STONES.map((item) => {
              const isSelected = selectedPlanets.includes(item.planet);
              return (
                <button
                  key={item.planet}
                  onClick={() => togglePlanet(item.planet)}
                  className={`p-3 rounded-xl border text-left transition-all flex items-center justify-between cursor-pointer ${
                    isSelected
                      ? 'border-astro-primary bg-astro-primary/10 shadow-xs'
                      : 'border-astro-border bg-astro-bg hover:border-astro-primary/40'
                  }`}
                >
                  <div className="flex items-center gap-2.5 truncate">
                    <div
                      className="w-3.5 h-3.5 rounded-full shrink-0 shadow-xs"
                      style={{ backgroundColor: item.color }}
                    />
                    <div className="truncate">
                      <div className="text-xs font-bold text-astro-text truncate">{item.gemstone}</div>
                      <div className="text-[10px] text-astro-secondary-text truncate">{item.label}</div>
                    </div>
                  </div>
                  {isSelected && (
                    <span className="w-5 h-5 rounded-full bg-astro-primary text-white flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Audit Verdict Banner */}
      <div
        className={`border rounded-2xl p-5 shadow-sm space-y-3 ${
          auditResult.overallCompatibility === 'SEVERE_CLASH'
            ? 'bg-rose-500/10 border-rose-500/30'
            : auditResult.overallCompatibility === 'CAUTION_FRICTION'
            ? 'bg-amber-500/10 border-amber-500/30'
            : 'bg-emerald-500/10 border-emerald-500/30'
        }`}
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {auditResult.overallCompatibility === 'SEVERE_CLASH' ? (
              <ShieldAlert className="w-7 h-7 text-rose-600 shrink-0" />
            ) : auditResult.overallCompatibility === 'CAUTION_FRICTION' ? (
              <AlertTriangle className="w-7 h-7 text-amber-600 shrink-0" />
            ) : (
              <ShieldCheck className="w-7 h-7 text-emerald-600 shrink-0" />
            )}
            <div>
              <h4
                className={`font-serif font-bold text-lg ${
                  auditResult.overallCompatibility === 'SEVERE_CLASH'
                    ? 'text-rose-900'
                    : auditResult.overallCompatibility === 'CAUTION_FRICTION'
                    ? 'text-amber-900'
                    : 'text-emerald-900'
                }`}
              >
                {auditResult.verdictTitle}
              </h4>
              <p className="text-xs text-astro-text/80 leading-relaxed max-w-2xl mt-0.5">
                {auditResult.verdictSummary}
              </p>
            </div>
          </div>

          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase font-bold text-astro-secondary-text">Harmony Score</div>
            <div
              className={`text-2xl font-bold font-mono ${
                auditResult.overallCompatibility === 'SEVERE_CLASH'
                  ? 'text-rose-700'
                  : auditResult.overallCompatibility === 'CAUTION_FRICTION'
                  ? 'text-amber-700'
                  : 'text-emerald-700'
              }`}
            >
              {auditResult.compatibilityScore}%
            </div>
          </div>
        </div>
      </div>

      {/* Pairwise Interaction Cards */}
      {auditResult.pairwiseAudits.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-bold text-sm text-astro-text flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-astro-primary" /> Pairwise Planetary Dynamics & Interference Matrix
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {auditResult.pairwiseAudits.map((pair, idx) => {
              const badge = getStatusBadge(pair.status);
              return (
                <div key={idx} className="bg-astro-card border border-astro-border rounded-xl p-4 space-y-2.5">
                  <div className="flex items-center justify-between gap-2 border-b border-astro-border/50 pb-2">
                    <span className="font-bold text-xs text-astro-text">
                      {pair.gemA} + {pair.gemB}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${badge.className}`}>
                      {badge.label}
                    </span>
                  </div>
                  <p className="text-xs text-astro-secondary-text leading-relaxed">
                    {pair.explanation}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Individual Chart Alignment Status for Each Selected Gem */}
      {auditResult.chartSafetyNotes.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-bold text-sm text-astro-text flex items-center gap-2">
            <Info className="w-4 h-4 text-astro-primary" /> Chart Compatibility for {kundali.houses[0]?.sign} Lagna
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {auditResult.chartSafetyNotes.map((note, idx) => (
              <div
                key={idx}
                className={`p-3.5 rounded-xl border space-y-1.5 ${
                  note.isSafeForChart
                    ? 'bg-astro-bg border-astro-border'
                    : 'bg-rose-500/5 border-rose-500/20'
                }`}
              >
                <div className="flex items-center justify-between">
                  <strong className="text-xs text-astro-text">{note.gem}</strong>
                  <span
                    className={`text-[10px] font-bold px-2 py-0.2 rounded ${
                      note.isSafeForChart
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-rose-100 text-rose-800'
                    }`}
                  >
                    {note.isSafeForChart ? 'Safe for Chart' : 'High Risk'}
                  </span>
                </div>
                <p className="text-[11px] text-astro-secondary-text leading-relaxed">
                  {note.reason}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
